// ─── GBCOSPACK — inline PDF canvas preview (Template Studio) ───────────────
// Renders QuotationPdfDocument to a real <canvas> via pdfjs-dist instead of
// @react-pdf/renderer's <PDFViewer>. The native iframe viewer relies on the
// browser's built-in PDF renderer, which is unavailable inside the Readdy
// preview sandbox (it shows a blob download link). Canvas rendering is fully
// self-contained and sandbox-safe.

import { useState, useEffect, useRef } from 'react';
import { createElement } from 'react';
import type { QuotationDocumentData } from '@/quotation/document/types';
import { registerPdfFontsForLocale } from '@/quotation/pdf/fonts';
import * as pdfjsLib from 'pdfjs-dist';
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&inline';

pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();

function PdfPageCanvas({
  pdf,
  pageNumber,
  scale,
}: {
  pdf: pdfjsLib.PDFDocumentProxy;
  pageNumber: number;
  scale: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    let renderTask: pdfjsLib.RenderTask | null = null;
    let page: pdfjsLib.PDFPageProxy | null = null;

    (async () => {
      try {
        page = await pdf.getPage(pageNumber);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });
        if (cancelled) return;
        setSize({ w: viewport.width, h: viewport.height });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        renderTask = page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
        });
        await renderTask.promise;
      } catch {
        // render cancelled or document destroyed — safe to ignore
      }
    })();

    return () => {
      cancelled = true;
      if (renderTask) renderTask.cancel();
      if (page) page.cleanup();
    };
  }, [pdf, pageNumber, scale]);

  const fallbackW = 595 * scale;
  const fallbackH = 842 * scale;

  return (
    <canvas
      ref={canvasRef}
      className="block bg-white"
      style={{
        width: size?.w ?? fallbackW,
        height: size?.h ?? fallbackH,
      }}
    />
  );
}

export default function PdfCanvasPanel({ data }: { data: QuotationDocumentData }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ stage: string; message: string } | null>(null);
  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [baseWidth, setBaseWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const pdfRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);

  // Generate the PDF blob from the SAME canonical data, then load it into
  // pdfjs-dist for canvas rendering.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNumPages(0);
    setBaseWidth(0);

    (async () => {
      try {
        await registerPdfFontsForLocale(data.documentLocale);
        const { pdf: renderPdf } = await import('@react-pdf/renderer');
        const QuotationPdfDocument = (
          await import('@/quotation/pdf/QuotationPdfDocument')
        ).default;
        const blob = await renderPdf(
          createElement(QuotationPdfDocument, { data }),
        ).toBlob();
        const arrayBuffer = await blob.arrayBuffer();
        if (cancelled) return;
        const doc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        if (cancelled) {
          doc.destroy();
          return;
        }
        pdfRef.current = doc;
        setPdf(doc);
        setNumPages(doc.numPages);
        const firstPage = await doc.getPage(1);
        const vp = firstPage.getViewport({ scale: 1 });
        firstPage.cleanup();
        setBaseWidth(vp.width);
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.error('[PdfCanvasPanel] render failed:', err);
          const message =
            err instanceof Error ? err.message : String(err);
          let stage = 'render';
          if (message.includes('font')) stage = 'font';
          else if (message.includes('dynamic') || message.includes('import')) stage = 'import';
          else if (message.includes('Blob') || message.includes('blob')) stage = 'blob';
          else if (message.includes('pdf') || message.includes('PDF')) stage = 'pdf';
          setError({ stage, message });
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      const doc = pdfRef.current;
      pdfRef.current = null;
      if (doc) {
        try {
          doc.destroy();
        } catch {
          // already destroyed
        }
      }
      setPdf(null);
    };
  }, [data.quotationNumber, data.documentLocale, data.documentType, reloadKey]);

  // Measure the container width so pages fit to the panel (like fit-to-width).
  useEffect(() => {
    if (loading || numPages === 0) return;
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, numPages]);

  if (loading) {
    return (
      <div className="h-full min-h-[500px] flex items-center justify-center bg-stone-100 text-stone-400 gap-2">
        <i className="ri-loader-4-line animate-spin text-xl" />
        <span className="text-sm">Loading PDF engine...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full min-h-[500px] flex flex-col items-center justify-center gap-3 bg-stone-100 text-stone-500 p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-rose-500 text-xl" />
        </div>
        <p className="text-sm font-semibold">Unable to render the PDF.</p>
        <p className="text-[11px] text-stone-400">Stage: {error.stage}</p>
        <p className="max-w-md text-[11px] font-mono text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2 break-all">
          {error.message}
        </p>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="px-4 py-2 text-xs font-semibold text-white bg-stone-800 rounded-lg hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          Retry
        </button>
      </div>
    );
  }

  const scale =
    containerWidth > 0 && baseWidth > 0
      ? (containerWidth - 32) / baseWidth
      : 0.8;

  const pages = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <div
      ref={containerRef}
      className="h-full min-h-[500px] overflow-y-auto bg-stone-100"
    >
      <div className="flex flex-col items-center gap-4 py-4 px-4">
        {pages.map((n) => (
          <div
            key={n}
            className="bg-white border border-stone-200 rounded-sm overflow-hidden"
          >
            <PdfPageCanvas pdf={pdf!} pageNumber={n} scale={scale} />
            <div className="flex items-center justify-center py-1.5 text-[11px] font-medium text-stone-400 border-t border-stone-100">
              {n} / {numPages}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}